import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

class ProductSpecRows extends HTMLElement {
  connectedCallback() {
    document.addEventListener(ThemeEvents.variantUpdate, this.updateSpecRows);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.updateSpecRows);
  }

  /**
   * Refreshes the variant-dependent spec-table rows (SKU, MPN, EAN, dimensions,
   * weights, EPREL ID, gaming refresh rate, energy label / product information
   * sheet links) when the selected variant changes.
   *
   * This section is a sibling top-level section to the main product-information
   * section, not a descendant of it, so it cannot rely on `.closest('.shopify-section')`
   * the way the MPN & Energie block does - the variant:update event never reaches
   * that ancestor chain. Listening on `document` instead catches the same bubbled
   * event regardless of which section dispatched it.
   *
   * Follows the same independent-fetch-of-current-URL pattern already proven in
   * product-compliance.js (preserves a `?view=` template-preview override, which
   * the core variant picker's own shared fetch does not).
   * @param {CustomEvent} event - The variant update event.
   */
  updateSpecRows = async (event) => {
    if (event.detail.data?.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const variantId = event.detail.resource?.id;
    if (!variantId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);

    let html;
    try {
      const response = await fetch(url.toString());
      if (!response.ok) return;
      html = new DOMParser().parseFromString(await response.text(), 'text/html');
    } catch (error) {
      return;
    }

    const targets = this.querySelectorAll('[data-spec-reactive]');
    targets.forEach((target) => {
      const key = target.getAttribute('data-spec-reactive');
      const freshTarget = html.querySelector(`[data-spec-reactive="${key}"]`);
      if (freshTarget) morph(target, freshTarget, { childrenOnly: true });
    });
  };
}

if (!customElements.get('product-spec-rows')) {
  customElements.define('product-spec-rows', ProductSpecRows);
}
